import { defaultOnOf } from "../settings/catalog";

/**
 * LES PORTES DES INSCRIPTIONS — l'heure à laquelle elles s'ouvrent, celle à laquelle
 * elles se ferment, et l'interrupteur qui ferme sur-le-champ.
 *
 * ⚠️ LES DEUX HEURES VIVENT DANS LE MÊME MODULE, et ce n'est pas un fourre-tout : elles
 * partagent TOUT — la forme rangée, la lecture de l'instant, le champ du panneau, la
 * légende qui nomme le fuseau. Séparées, la moitié de ce fichier aurait été recopiée, et
 * c'est la copie qu'on aurait oublié de corriger le jour où l'une des deux se trompe
 * d'une heure au passage à l'heure d'été.
 *
 * L'organisateur disposait d'un seul geste : « Clore les inscriptions », à la main, le
 * jour dit. Il fallait donc être devant son écran à l'heure dite — et une clôture
 * annoncée aux participants pour mardi midi tombait en réalité quand quelqu'un y
 * repensait. L'échéance la pose une fois pour toutes.
 *
 * ═══ ⚠️ UN INSTANT, ET NON UNE HEURE MURALE — LA DOCTRINE EST INVERSE ICI ═══
 *
 * Partout ailleurs dans ce tunnel (`funnel/conferences.ts`, `funnel/dates.ts`,
 * `settings/complete.ts`) une date se lit en UTC PARCE QU'ELLE EST MURALE : « 14:00 »
 * saisi au back office s'affiche « 14:00 » à Paris comme à Séoul, puisque c'est l'heure
 * à laquelle on se présente DANS la salle. Une clôture, elle, n'a pas de salle : c'est
 * un ÉVÉNEMENT UNIQUE, qui arrive au même moment pour tout le monde.
 *
 * Si l'organisateur parisien ferme à 14 h, le participant coréen voit la porte se
 * fermer à 21 h chez lui — c'est le MÊME instant, et c'est ce qu'on veut. Lue comme une
 * heure murale, la clôture aurait laissé les Coréens s'inscrire huit heures après les
 * Parisiens, et l'organisateur aurait découvert après coup une liste qui continuait de
 * grossir alors qu'il l'avait close.
 *
 * D'où la forme rangée : un ISO 8601 AVEC DÉCALAGE, `2026-09-21T14:00:00+02:00`.
 * Elle porte les deux choses à la fois — l'instant, que tout le monde lit pareil, et
 * l'heure que l'organisateur a TAPÉE, celle qu'il citera au support.
 */

/** L'interrupteur : clore maintenant, à la main. */
export const CLOSED_KEY = "FO_MEETING_V2_REGISTRATION_CLOSED";
/** L'échéance : clore de soi-même, à un instant. */
export const CLOSED_AT_KEY = "FO_MEETING_V2_REGISTRATION_CLOSED_AT";
/**
 * L'AUTRE PORTE : n'ouvrir qu'à un instant, et pas avant.
 *
 * ⚠️ ELLE SE LIT EXACTEMENT COMME LA CLÔTURE — un ISO avec décalage, donc un INSTANT,
 * pour la raison écrite plus haut : une ouverture annoncée à 9 h à Paris s'ouvre à
 * 16 h à Séoul, au même moment pour tout le monde. Lue en heure murale, les Coréens
 * seraient entrés sept heures avant les Parisiens.
 *
 * ⚠️ SON ABSENCE VAUT « OUVERT », JAMAIS « FERMÉ ». C'est l'état de TOUS les événements
 * existants, qui n'ont pas cette clé : lue à l'envers, cette nouveauté aurait verrouillé
 * d'un coup chaque parcours en cours.
 */
export const OPEN_AT_KEY = "FO_MEETING_V2_REGISTRATION_OPEN_AT";

const two = (n: number) => String(n).padStart(2, "0");

/** Un ISO qui dit de quel instant il parle : `Z`, `+02:00` ou `+0200`. */
const SIGNED = /(?:Z|[+-]\d{2}:?\d{2})$/i;
/** Un ISO qui porte une heure — donc à qui il manque un décalage, le cas échéant. */
const WITH_TIME = /T\d{2}:\d{2}/;

/**
 * L'ÉCHÉANCE RANGÉE → L'INSTANT, en millisecondes. `null` quand il n'y en a pas.
 *
 * ⚠️ UN ISO SANS DÉCALAGE EST LU EN UTC, ET SÛREMENT PAS DANS LE FUSEAU DU LECTEUR.
 * `Date.parse("2026-09-21T14:00")` rend l'heure LOCALE de qui l'exécute : le panneau
 * écrit toujours le décalage, mais une valeur posée à la main en base ne l'aurait pas,
 * et elle aurait clos à 14 h DANS CHAQUE PAYS — exactement le défaut que ce module
 * existe pour empêcher. En UTC, la valeur reste au moins la même pour tous, et c'est
 * aussi la convention du reste du produit pour une chaîne sans fuseau.
 */
export function instantOf(raw: unknown): number | null {
  if (typeof raw !== "string") return null;
  const written = raw.trim();
  if (written === "") return null;

  const ms = Date.parse(
    WITH_TIME.test(written) && !SIGNED.test(written) ? `${written}Z` : written,
  );
  return Number.isNaN(ms) ? null : ms;
}

/**
 * LES INSCRIPTIONS SONT-ELLES CLOSES ? La question que posent le fil du tunnel et
 * l'écran de participation, et ils doivent y répondre PAREIL — un fil qui laisse
 * avancer vers un écran verrouillé est un cul-de-sac.
 *
 * ⚠️ L'ÉCHÉANCE ATTEINTE CLÔT : à 14:00:00 pile, c'est fermé. « Clore à 14 h » se
 * comprend comme « la dernière inscription est à 13:59 », pas comme « une minute de
 * grâce ».
 *
 * ⚠️ L'HEURE EST CELLE DE L'APPELANT, PAS UN TIC. Le verrou se pose au moment où
 * l'écran se dessine, comme n'importe quel autre réglage : un onglet déjà ouvert garde
 * son formulaire, et c'est voulu — arracher un formulaire à moitié rempli des mains de
 * quelqu'un lui ferait perdre sa saisie sans rien garantir en retour, puisque
 * l'écriture, elle, ne passe pas par ce réglage.
 */
export function closedNow(
  read: (key: string) => unknown,
  now: number = Date.now(),
): boolean {
  const raw = read(CLOSED_KEY);
  const byHand = typeof raw === "boolean" ? raw : defaultOnOf(CLOSED_KEY);
  if (byHand) return true;

  return closingPassed(read(CLOSED_AT_KEY), now);
}

/**
 * L'ÉCHÉANCE DE CLÔTURE EST-ELLE DÉJÀ ATTEINTE ? Ce que la porte applique, et ce que la
 * note du panneau annonce sous le champ.
 *
 * ⚠️ UNE SEULE RÈGLE POUR LES DEUX. Une erreur d'année ferme un événement en cours sans
 * que rien ne le dise : le panneau doit prévenir EXACTEMENT quand la porte se ferme, ni
 * une minute avant (une alerte à tort apprend à ne plus les lire), ni une minute après.
 */
export function closingPassed(
  raw: unknown,
  now: number = Date.now(),
): boolean {
  const at = instantOf(raw);
  return at !== null && now >= at;
}

/**
 * LES INSCRIPTIONS NE SONT PAS ENCORE OUVERTES.
 *
 * ⚠️ L'HEURE DITE OUVRE, elle ne fait pas attendre une minute de plus — l'inverse exact
 * de la clôture juste au-dessus, et c'est voulu : « ouvrir à 9 h » se comprend comme
 * « la première inscription est à 9:00:00 », « clore à 14 h » comme « la dernière est à
 * 13:59 ». Les deux bornes prises dans le même sens auraient laissé une minute morte à
 * chaque bout, ou ouvert une porte déjà close.
 *
 * ⚠️ UNE HEURE ILLISIBLE N'ENFERME PERSONNE, comme pour la clôture : mieux vaut une
 * inscription trop tôt qu'un événement entier verrouillé par une faute de frappe en
 * base — et là, personne n'aurait pu s'inscrire du tout.
 */
export function notOpenYet(
  read: (key: string) => unknown,
  now: number = Date.now(),
): boolean {
  const at = instantOf(read(OPEN_AT_KEY));
  return at !== null && now < at;
}

/**
 * L'ÉTAT DE LA PORTE — et les trois mots que le fil et l'écran ont à dire.
 *
 * `"open"` on entre, `"closed"` c'est fini, `"not-yet"` ce n'est pas encore l'heure.
 *
 * ⚠️ UNE SEULE FONCTION POUR LES DEUX PORTES, PARCE QU'ELLES FERMENT LE MÊME PARCOURS.
 * Le fil s'arrête à son premier écran dans les deux cas, et cet écran doit dire LEQUEL :
 * « inscriptions closes » devant une porte qui n'a pas encore ouvert envoie au support
 * quelqu'un qui n'avait qu'à revenir le lendemain.
 *
 * ⚠️ LA CLÔTURE PASSE DEVANT, et c'est la seule réponse sûre à une configuration qui se
 * contredit (une ouverture postérieure à la clôture, ou l'interrupteur mis alors qu'une
 * ouverture est encore à venir). L'interrupteur est le geste immédiat de l'organisateur,
 * et « c'est fini » est l'état final : annoncer « bientôt » à sa place promettrait une
 * porte qui ne s'ouvrira jamais.
 */
export type Door = "open" | "closed" | "not-yet";

export function doorNow(
  read: (key: string) => unknown,
  now: number = Date.now(),
): Door {
  if (closedNow(read, now)) return "closed";
  return notOpenYet(read, now) ? "not-yet" : "open";
}

/**
 * L'INSTANT → LE CHAMP DU PANNEAU (`datetime-local`), dans le fuseau de QUI LIT.
 *
 * ⚠️ DANS SON FUSEAU À LUI, et c'est le corollaire honnête de l'instant. Un second
 * organisateur qui ouvre le panneau depuis Séoul voit « 21:00 » là où son collègue
 * parisien avait tapé « 14:00 » : ce n'est pas une dérive, c'est la même seconde, et
 * la légende sous le champ nomme le fuseau pour que ça se lise ainsi. Lui remontrer
 * « 14:00 » sans plus de façon lui ferait croire que la porte ferme après son déjeuner.
 */
export function fieldFromInstant(raw: unknown): string {
  const ms = instantOf(raw);
  if (ms === null) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(
    d.getHours(),
  )}:${two(d.getMinutes())}`;
}

/**
 * LE CHAMP DU PANNEAU → L'ÉCHÉANCE RANGÉE. La chaîne vide vaut « pas d'échéance » :
 * c'est ce que rend le navigateur tant que la date est incomplète, et ce qu'il rend
 * quand on vide le champ.
 *
 * ⚠️ LE DÉCALAGE EST CELUI DE LA DATE VISÉE, PAS CELUI D'AUJOURD'HUI. Une clôture de
 * juillet réglée en janvier depuis Paris vaut `+02:00`, pas `+01:00` :
 * `getTimezoneOffset()` est interrogé SUR LA DATE construite, donc l'heure d'été est
 * prise en compte. Pris sur `new Date()`, il aurait décalé la clôture d'une heure —
 * une heure entière d'inscriptions en trop, ou en moins.
 *
 * ⚠️ LES COMPOSANTES SONT RELUES SUR LA DATE, jamais recopiées du champ. Une heure
 * murale qui n'existe pas (2 h 30 la nuit du passage à l'heure d'été) est normalisée
 * par `Date` en 3 h 30 ; recopier le champ aurait écrit « 02:30+02:00 », c'est-à-dire
 * un instant qui n'est pas celui que la valeur prétend nommer.
 */
export function instantFromField(field: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(field.trim());
  if (!parts) return "";

  const [, year, month, day, hour, minute] = parts;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    0,
    0,
  );
  if (Number.isNaN(date.getTime())) return "";

  const offset = -date.getTimezoneOffset();
  const sign = offset < 0 ? "-" : "+";
  const abs = Math.abs(offset);
  return (
    `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}` +
    `T${two(date.getHours())}:${two(date.getMinutes())}:00` +
    `${sign}${two(Math.floor(abs / 60))}:${two(abs % 60)}`
  );
}

/**
 * LE FUSEAU DANS LEQUEL LE CHAMP SE LIT — « Europe/Paris (UTC+2) ».
 *
 * ⚠️ LE DÉCALAGE EST CELUI DE L'INSTANT REGARDÉ, pour la raison d'au-dessus : une
 * clôture d'août annoncée « UTC+1 » en plein hiver aurait été fausse d'une heure.
 */
export function zoneLabel(at: Date = new Date()): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -at.getTimezoneOffset();
  const sign = offset < 0 ? "-" : "+";
  const abs = Math.abs(offset);
  const minutes = abs % 60;
  return `${zone} (UTC${sign}${Math.floor(abs / 60)}${
    minutes ? `:${two(minutes)}` : ""
  })`;
}

/**
 * L'INSTANT ÉCRIT EN UTC — « 21/09/2026 12:00 UTC ».
 *
 * C'est la moitié du message qui lève l'ambiguïté : deux organisateurs de deux
 * continents lisent deux heures différentes dans le champ, et la même ligne dessous.
 * Sans elle, chacun croit que l'autre s'est trompé.
 */
export function utcStamp(ms: number): string {
  const d = new Date(ms);
  return (
    `${two(d.getUTCDate())}/${two(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ` +
    `${two(d.getUTCHours())}:${two(d.getUTCMinutes())} UTC`
  );
}

/**
 * LA LÉGENDE SOUS LE CHAMP, en français : elle ne s'affiche qu'à l'organisateur, comme
 * le reste du panneau.
 */
export function zoneNote(raw: unknown): string {
  const ms = instantOf(raw);
  if (ms === null)
    return `Heure de ${zoneLabel()} — le même instant pour tous les participants.`;
  return `${zoneLabel(new Date(ms))} — soit ${utcStamp(ms)}, le même instant pour tous les participants.`;
}
